<template>
  <div>
    <!-- Switching on creates (simple types), switching off ends the feature, both via a dialog.
         The key re-renders the switch so it shows the real state again if the dialog is cancelled. -->
    <div class="d-flex align-center">
      <!-- Disabled inputs swallow clicks, so the switch stays enabled while the arrow is usable -->
      <v-switch
        :key="switchKey"
        :input-value="!!activeFeature"
        color="primary"
        :disabled="!isInteractive"
        :class="{ 'disable-events': !isInteractive }"
        @change="onToggle"
      >
        <template #label>
          <!-- Not `disabled`: Vuetify drops the color on disabled buttons, hiding the inherit state -->
          <v-btn
            icon
            :tabindex="canToggleInherit ? 0 : -1"
            :style="{ pointerEvents: canToggleInherit ? null : 'none', visibility: isInheritable ? null : 'hidden' }"
            :color="inheritFeature && inheritFeature.inherit ? 'primary' : ''"
            @click.stop="$emit('toggle-inherit', editableFeature, label)"
          >
            <v-icon>{{ mdiTransferDown }}</v-icon>
          </v-btn>
          {{ label }}
        </template>
      </v-switch>
      <help-tooltip
        v-if="help"
        :text="help"
      />
    </div>
    <div
      v-if="captionText || editableFeature"
      class="text-caption d-flex align-center flex-wrap"
    >
      <span
        v-if="captionText"
        class="mr-1"
      >{{ captionText }}</span>
      <v-btn
        v-if="editableFeature"
        x-small
        text
        color="primary"
        class="px-1"
        @click="$emit('edit', editableFeature, label, type, help)"
      >
        Bearbeiten
      </v-btn>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { mdiTransferDown } from '@mdi/js'
import { isValidAt, lastValidDay } from '@/lib/sort-utils'
import HelpTooltip from '@/components/UI/HelpTooltip.vue'
import { NON_INHERITABLE_FEATURE_TYPES } from '@/lib/settings-tools'

// Superusers can edit/end features here and create the simple types (feature/ endpoint);
// types needing payment devices, slugs or templates are created in Django admin.
export default {
  name: 'FeatureSwitch',
  components: { HelpTooltip },
  props: {
    // Explanation shown on hover next to the switch
    help: {
      type: String,
      default: null
    },
    label: {
      type: String,
      required: true
    },
    features: {
      type: Array,
      default: () => []
    },
    // URL of the organization whose page shows this switch
    organizationUrl: {
      type: String,
      default: null
    },
    // Backend FeatureType value of this switch
    type: {
      type: String,
      default: null
    },
    // Whether this type can be created in the frontend (types without extra configuration)
    creatable: {
      type: Boolean,
      default: false
    },
    canManage: {
      type: Boolean,
      default: false
    }
  },
  emits: ['edit', 'end', 'toggle-inherit'],
  data () {
    return { mdiTransferDown, switchKey: 0 }
  },
  computed: {
    ...mapState(['now']),
    // A parent's feature of a non-inheritable type does not apply here, even if it is listed
    relevantFeatures () {
      return this.isInheritable
        ? this.features
        : this.features.filter(f => !f.organization || f.organization === this.organizationUrl)
    },
    // The organization's own feature wins over one inherited from a parent, so it stays manageable
    activeFeature () {
      const active = this.relevantFeatures.filter(f => isValidAt(f, this.now))
      return active.find(this.isOwn) ?? active[0] ?? null
    },
    upcomingFeature () {
      return this.relevantFeatures
        .filter(f => f.valid_from && this.now.isBefore(f.valid_from))
        .sort((a, b) => moment(a.valid_from).diff(moment(b.valid_from)))[0] ?? null
    },
    lastExpiredFeature () {
      return this.relevantFeatures
        .filter(f => f.valid_notafter && !this.now.isBefore(f.valid_notafter))
        .sort((a, b) => moment(b.valid_notafter).diff(moment(a.valid_notafter)))[0] ?? null
    },
    // Features inherited from a parent org belong to the parent: they are managed there
    isInherited () {
      return !!this.activeFeature?.organization && this.activeFeature.organization !== this.organizationUrl
    },
    isOwn () {
      return feature => !!feature?.id && feature.organization === this.organizationUrl
    },
    editableFeature () {
      if (!this.canManage) return null
      return [this.activeFeature, this.upcomingFeature].find(this.isOwn) ?? null
    },
    canEndFeature () {
      return this.canManage && this.isOwn(this.activeFeature)
    },
    // veranstaltung.app slugs are unique and become the pretix organizer: an organization's
    // expired feature is reopened (same slug, events stay) instead of creating a new one
    reactivatableFeature () {
      if (this.type !== 'veranstaltung_app' || !this.isOwn(this.lastExpiredFeature)) return null
      return this.lastExpiredFeature
    },
    canCreate () {
      return this.canManage && this.creatable && !!this.type && !this.activeFeature && !this.upcomingFeature
    },
    isToggleable () {
      return this.canCreate || this.canEndFeature
    },
    // Inheritance is switched with the arrow, like for the settings
    isInheritable () {
      return !NON_INHERITABLE_FEATURE_TYPES.includes(this.type)
    },
    canToggleInherit () {
      return this.isInheritable && !!this.editableFeature
    },
    inheritFeature () {
      return this.activeFeature ?? this.upcomingFeature
    },
    isInteractive () {
      return this.isToggleable || this.canToggleInherit
    },
    captionText () {
      return [this.validityText, this.isInherited ? 'vererbt' : ''].filter(Boolean).join(', ')
    },
    validityText () {
      if (this.activeFeature) {
        const { valid_from: from, valid_notafter: until } = this.activeFeature
        if (!from && !until) {
          return ''
        }
        return [
          'gültig',
          from ? `ab ${this.format(from)}` : '',
          until ? `bis ${lastValidDay(until).format('DD.MM.YYYY')}` : ''
        ].filter(Boolean).join(' ')
      }
      if (this.upcomingFeature) {
        return `gültig ab ${this.format(this.upcomingFeature.valid_from)}`
      }
      if (this.lastExpiredFeature) {
        return `abgelaufen am ${this.format(this.lastExpiredFeature.valid_notafter)}`
      }
      return ''
    }
  },
  methods: {
    onToggle (on) {
      if (on && this.canCreate) {
        // Reactivate instead of creating, see reactivatableFeature
        this.$emit('edit', this.reactivatableFeature, this.label, this.type, this.help, !!this.reactivatableFeature)
      } else if (!on && this.canEndFeature) {
        this.$emit('end', this.activeFeature, this.label)
      }
      this.switchKey++
    },
    format (date) {
      return moment(date).format('DD.MM.YYYY')
    }
  }
}
</script>
